import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useCurrentPlan, useSubscriptionStatus, useBillingPortal, commonQueryKeys } from '../hooks/useCommon';
import { useAuth } from '../hooks/useAuth';
import { getPlanLifecycle, formatBillingDate } from '../lib/utils';
import PlansDisplay from './PlansDisplay';
import PageSection from '../components/PageSection';
import { styled } from '@mui/material/styles';

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  margin: theme.spacing(1),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
  },
  transition: 'all 0.3s ease',
}));

const PrimaryButton = styled(ActionButton)(() => ({
  background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
  boxShadow: '0 4px 15px rgba(139, 156, 255, 0.4)',
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a6fd8 0%, #d94f7e 100%)',
    boxShadow: '0 6px 20px rgba(139, 156, 255, 0.6)',
  },
}));

const SecondaryButton = styled(ActionButton)(() => ({
  border: '2px solid #6366F1',
  color: '#6366F1',
  '&:hover': {
    backgroundColor: '#6366F120',
    borderColor: '#4338CA',
  },
}));

interface BillingPageProps {}

const BillingPage: React.FC<BillingPageProps> = () => {
  const { t } = useTranslation('billing');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: currentPlan, isLoading: planLoading } = useCurrentPlan(user?.id);
  const { data: subscriptionStatus, isLoading: subscriptionLoading } = useSubscriptionStatus(user?.id);
  const billingPortal = useBillingPortal();

  const isLoading = planLoading || subscriptionLoading;
  const hasActiveSubscription = subscriptionStatus && 
    (subscriptionStatus.status === 'active' || subscriptionStatus.status === 'trialing' || subscriptionStatus.status === 'past_due');
  
  const lifecycle = getPlanLifecycle(subscriptionStatus);

  const getStatusChipColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'canceling') return 'error';
    if (statusLower === 'active') return 'success';
    if (statusLower === 'trialing') return 'info';
    if (statusLower === 'past_due') return 'warning';
    if (statusLower === 'canceled') return 'error';
    return 'default';
  };

  const getStatusLabel = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'canceling') return t('status.canceling');
    if (statusLower === 'active') return t('status.active');
    if (statusLower === 'trialing') return t('status.trialing');
    if (statusLower === 'past_due') return t('status.pastDue');
    if (statusLower === 'canceled') return t('status.canceled');
    return status;
  };

  // Refetch plan and subscription data when component mounts (e.g., after returning from portal)
  useEffect(() => {
    if (user?.id) {
      queryClient.invalidateQueries({ 
        queryKey: [...commonQueryKeys.currentPlan(), user.id] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...commonQueryKeys.subscriptionStatus(), user.id] 
      });
    }
  }, [user?.id, queryClient]);

  const handleBillingSettings = async () => {
    try {
      // Invalidate queries before redirecting (they'll refetch when user returns)
      if (user?.id) {
        queryClient.invalidateQueries({ 
          queryKey: [...commonQueryKeys.currentPlan(), user.id] 
        });
        queryClient.invalidateQueries({ 
          queryKey: [...commonQueryKeys.subscriptionStatus(), user.id] 
        });
      }
      await billingPortal.mutateAsync();
    } catch (error) {
      // Error is handled by the mutation's onError
    }
  };

  return (
    <PageSection 
      title={t('title')}
      subtitle={t('subtitle')}
      hideTitleOnMobile
    >
      <Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Current Plan Status Header */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                        {t('currentPlan.label')}
                      </Typography>
                      <Typography 
                        variant="h6" 
                        component="span"
                        sx={{ 
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        {currentPlan?.name || t('currentPlan.free')}
                      </Typography>
                      {hasActiveSubscription && subscriptionStatus && (
                        <>
                          <Chip 
                            label={lifecycle.isCanceling ? t('status.canceling') : getStatusLabel(lifecycle.statusLabel)}
                            color={getStatusChipColor(lifecycle.isCanceling ? 'canceling' : lifecycle.statusLabel)}
                            size="small"
                          />
                          {(lifecycle.renewsOn || lifecycle.cancelsOn) && (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                                •
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{
                                  fontWeight: 500,
                                  background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  backgroundClip: 'text',
                                }}
                              >
                                {lifecycle.isCanceling 
                                  ? t('currentPlan.cancelsOn', { date: formatBillingDate(lifecycle.cancelsOn) })
                                  : t('currentPlan.renewsOn', { date: formatBillingDate(lifecycle.renewsOn) })}
                              </Typography>
                            </>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Billing Portal button - styled to match app */}
                  {hasActiveSubscription && (
                    <Button
                      variant="contained"
                      size="medium"
                      onClick={handleBillingSettings}
                      disabled={billingPortal.isPending}
                      startIcon={billingPortal.isPending ? <CircularProgress size={16} /> : <SettingsIcon />}
                      sx={{
                        textTransform: 'none',
                        minWidth: 180,
                        background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                        boxShadow: '0 4px 15px rgba(139, 156, 255, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a6fd8 0%, #d94f7e 100%)',
                          boxShadow: '0 6px 20px rgba(139, 156, 255, 0.6)',
                        },
                      }}
                    >
                      {billingPortal.isPending ? t('portal.opening') : t('portal.openBillingPortal')}
                    </Button>
                  )}
                </Box>
                
                {/* Payment failure banner - only show when past_due */}
                {subscriptionStatus?.status === 'past_due' && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {t('alerts.paymentFailed')}
                  </Alert>
                )}
                
                {/* Cancellation banner - only show when canceling */}
                {lifecycle.isCanceling && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {t('alerts.canceling')}
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Info message for free users - only show if no active subscription AND current plan has no cost */}
            {!hasActiveSubscription && (!currentPlan || currentPlan.price === 0) && (
              <Box sx={{ mb: 4 }}>
                <Alert severity="info">
                  <Typography variant="body2">
                    {t('alerts.upgradeInfo')}
                  </Typography>
                </Alert>
              </Box>
            )}

            {/* Plans Grid */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
                {t('plans.title')}
              </Typography>
              <PlansDisplay />
            </Box>

            {/* Security Footer */}
            <Box 
              sx={{ 
                mt: 4, 
                p: 2.5, 
                backgroundColor: 'grey.50', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <LockIcon sx={{ color: 'success.main', fontSize: 28 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>{t('security.title')}</strong> {t('security.processedBy')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('security.compliance')}
                </Typography>
              </Box>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  opacity: 0.7,
                }}
              >
                STRIPE
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </PageSection>
  );
};

export default BillingPage;
